import { useState, useRef, useContext, useCallback } from "react";
import TechBankerSummaryGrid from "./tech-banker-summary-grid";
import TechBankerDetailGrid from "./tech-banker-detail-grid";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { SaveTechBankerHours } from "../../../../../services/api/dashboard-api";
import {  Tooltip } from "@fluentui/react-components";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { useFluentToast } from "../../../../../components/FluentUI/Fluent-Toast";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../../services/api/authentication-api";
import { useHistory } from "react-router-dom";
import {
  handleExport,
  getEditedRows,
  buildPayload,
  mergeUpdatedData,
  getSummaryColumns,
  getDetailColumns,
  computeExportData,
} from "./tech-banker-time-log-helper";
import {
  normalizeDate,
  getLastUpdateTooltip,
  submitButton
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import PropTypes from "prop-types";
import "./tech-banker-time-log.scss";

const TechBankerTimeLog = ({ items }) => {
  const [showDetails, setShowDetails] = useState(true);
  const [detailState, setDetailState] = useState({ sort: [], filter: null });
  const [summaryItem, setSummaryItem] = useState({
    ...items,
    LastUpdated: normalizeDate(items?.LastUpdated),
  });
  const [originalData, setOriginalData] = useState(
    (items.Projects || []).map((p, index) => ({
      ...p,
      inEdit: true,
      _badgeIndex: index + 1,
      LastUpdated: normalizeDate(p.LastUpdated),
    }))
  );
  const [data, setData] = useState(originalData);
  const [isSaving, setIsSaving] = useState(false);
  const { showToast, FluentToast } = useFluentToast();
  const user = useContext(LoginContext);
  const _detailExport = useRef(null);
  const _summaryExport = useRef(null);
  const history = useHistory();
  const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const handleItemChange = useCallback((e) => {
    const { field, value, dataItem } = e;
    const restrictedFields = ["LastWeekHours", "WeekendHours", "CurrentWeekHours"];
    if (restrictedFields.includes(field) && value < 0) return;
    setData((prev) =>
      prev.map((row) =>
        row.ProjectId === dataItem.ProjectId ? { ...row, [field]: value } : row
      )
    );
  }, []);

  const handleSubmit = async () => {
    const editedRows = getEditedRows(data, originalData);
    if (editedRows.length === 0) return;

    const payload = buildPayload(editedRows);
    setIsSaving(true);

    try {
      const response = await SaveTechBankerHours(payload);
      const updated = response?.data;

      if (
        !updated ||
        !Array.isArray(updated.Projects) ||
        updated.Projects.length === 0
      ) {
        showToast("Save Failed", "Could not save your changes.", "error");
        return;
      }

      const merged = mergeUpdatedData(data, updated.Projects);

      setData(merged);
      setOriginalData(merged);
      setSummaryItem({
        ...summaryItem,
        AllDealsHours: updated.AllDealsHours,
        TotalLastWeekHours: updated.TotalLastWeekHours,
        TotalWeekendHours: updated.TotalWeekendHours,
        TotalCurrentWeekHours: updated.TotalCurrentWeekHours,
        LastUpdated: normalizeDate(updated.LastUpdated),
        Projects: merged,
      });

      const now = new Date();
      showToast(
        "Edits Saved Successfully",
        `Changes to the projects were successfully saved on ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
        "success"
      );
    } catch (err) {
      console.error("Failed to submit:", err);
      showToast(
        "Save Failed",
        "An unexpected error occurred. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const editedRows = getEditedRows(data, originalData);
  const isSubmitDisabled = isSaving || editedRows.length === 0;
  const lastUpdated = summaryItem?.LastUpdated;
  const tooltipText = getLastUpdateTooltip(lastUpdated);
  const submitBtn = submitButton(isSubmitDisabled, handleSubmit);
  const hasAnyFilter =
    detailState.filter &&
    Array.isArray(detailState.filter.filters) &&
    detailState.filter.filters.length > 0;
  const canReset = detailState.sort.length > 0 || hasAnyFilter;
  const resetDataState = () => {
    setDetailState({ sort: [], filter: null });
  };
  return (
    <>
      <div className="tech-banker-time-log">
        <div className="widget-header">
          <div className="header-left">
            <div className="main-title-badge-row">
              <div className="main-title">Banker Time Log</div>
              <FluentBadge
                appearance="tint"
                size={"large"}
                text={`${items?.Projects?.length ?? 0} Active Deals`}
              />
            </div>
            <div className="sub-title">GIB/Technology</div>
          </div>
          <div className="header-right">
            <Tooltip content="Reset Filters and Sorting">
              <span>
                <FluentButton
                  appearance="secondaryWithOutline"
                  icon={
                    <FluentUiIcon
                      name="ArrowResetRegular"
                      color={canReset ? "#0F6CBD" : "#A0A0A0"}
                      size={20}
                      disabled={!canReset}
                    />
                  }
                  disabled={!canReset}
                  onClick={resetDataState}
                />
              </span>
            </Tooltip>

            <Tooltip content="Export Data">
              <span>
                <FluentButton
                  appearance="secondaryWithOutline"
                  icon={
                    <FluentUiIcon
                      name="ArrowDownloadRegular"
                      color="#0F6CBD"
                      size={20}
                    />
                  }
                  onClick={() =>
                    handleExport({ summaryRef: _summaryExport, detailRef: _detailExport })
                  }
                />
              </span>
            </Tooltip>

            <FluentButton
              text="Create Deal"
              appearance="secondaryWithOutline"
              disabled={!canCreate}
              icon={
                <FluentUiIcon
                  name="FolderAddRegular"
                  color={canCreate ? "#0F6CBD" : "#A0A0A0"}
                  size={20}
                  disabled={!canCreate}
                />
              }
              onClick={() => {
                if (canCreate) {
                  history.push({
                    pathname: "/projects/create",
                    state: { fromDashboard: true },
                  });
                }
              }}
            />
            
            {tooltipText ? (
              <Tooltip
                content={tooltipText}
                relationship="label"
                positioning="below"
              >
                <div>{submitBtn}</div>
              </Tooltip>
            ) : ( submitBtn )}
          </div>
        </div>

        <ExcelExport data={[summaryItem]} ref={_summaryExport}>
          {getSummaryColumns()}
        </ExcelExport>

        <TechBankerSummaryGrid
          summaryItem={summaryItem}
          showDetails={showDetails}
          onToggle={() => setShowDetails(!showDetails)}
        />


        {showDetails && (
          <>
            <ExcelExport data={computeExportData(data)} ref={_detailExport}>
              {getDetailColumns()}
            </ExcelExport>

            <TechBankerDetailGrid 
              data={data} 
              onItemChange={handleItemChange} 
              canEdit={canEdit} 
              canCreate={canCreate}
              detailState={detailState}
              onDetailStateChange={setDetailState}
              userId={user?.userID}
            />

            {/* Following div is added to bring focus to submit button if the user is tabbing through the widget */}
            <div
              tabIndex={0}
              style={{ width: 0, height: 0, overflow: "hidden" }}
              onFocus={() => {
                const btn = document.getElementById("tb-submit-btn");
                if (btn) btn.focus();
              }}
            />
          </>
        )}
      </div>
      { FluentToast }
    </>
  );
};

TechBankerTimeLog.propTypes = {
  items: PropTypes.shape({
    Projects: PropTypes.arrayOf(
      PropTypes.shape({
        ProjectId: PropTypes.string,
        ProjectName: PropTypes.string,
        LastWeekHours: PropTypes.number,
        WeekendHours: PropTypes.number,
        TotalHours: PropTypes.number,
        CurrentWeekHours: PropTypes.number,
        LastUpdated: PropTypes.string,
      })
    ),
    LastUpdated: PropTypes.string,
    AllDealsHours: PropTypes.number,
    TotalLastWeekHours: PropTypes.number,
    TotalWeekendHours: PropTypes.number,
    TotalCurrentWeekHours: PropTypes.number,
    User: PropTypes.shape({
      Id: PropTypes.string,
      Name: PropTypes.string,
    }),
  }).isRequired,
};

export default TechBankerTimeLog;